import { ReactElement, useEffect, useState } from "react";
import useSound from "use-sound";
import { m } from "framer-motion";
// import { useCountUp } from "react-countup";
import CountUp from "react-countup";

interface giftItemProps {
    img: string;
    fx?: "none" | "slide" | "pop" | "stomp";
}

interface giftTransitionProps {
    children: ReactElement | ReactElement[];
    fx?: "none" | "slide" | "pop" | "stomp";
    className: string;
}

const GiftTransition: React.FC<giftTransitionProps> = ({
    children,
    className,
    fx = "none",
}) => {
    if (fx === "pop") {
        return (
            <m.div
                initial={{
                    scale: 0,
                    opacity: 0,
                }}
                animate={{
                    scale: 1,
                    opacity: 1,
                }}
                transition={{
                    duration: 0.3,
                    stiffness: 250,
                    type: "spring",
                }}
                className={className}
            >
                {children}
            </m.div>
        );
    } else if (fx === "stomp") {
        return (
            <m.div
                initial={{
                    opacity: 0,
                    scale: 3,
                }}
                animate={{
                    opacity: 1,
                    scale: 1,
                }}
                transition={{
                    duration: 0.3,
                }}
                className={className}
            >
                {children}
            </m.div>
        );
    } else if (fx === "slide") {
        return (
            <m.div
                initial={{
                    x: "50px",
                    opacity: 1,
                    scale: 1,
                }}
                animate={{
                    x: "0px",
                    opacity: 1,
                    scale: 1,
                }}
                transition={{
                    duration: 0.3,
                }}
                className={className}
            >
                {children}
            </m.div>
        );
    }
    return <div className={className}>{children}</div>;
};

export const GiftItem: React.FC<giftItemProps> = ({ img, fx = "none" }) => {
    return (
        <GiftTransition className="w-24 h-24 rounded-full" fx={fx}>
            <img
                src={img}
                alt="gift"
                className="object-contain w-full h-full"
            />
        </GiftTransition>
    );
};

export const StreakFull = ({ timeout, fx, duration = 0.2 }) => {
    const [showFilled, setShowFilled] = useState(false);

    useEffect(() => {
        const timeoutId = setTimeout(
            () => {
                if (fx) {
                    setShowFilled(true);
                }
            },
            timeout ? timeout : 100,
        );
        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    if (fx) {
        return (
            <div className="relative w-10 h-10 rounded-full">
                {showFilled && (
                    <m.img
                        initial={{
                            scale: 0,
                            opacity: 0,
                        }}
                        animate={{
                            scale: 1,
                            opacity: 1,
                        }}
                        transition={{
                            duration,
                            stiffness: 250,
                            type: "spring",
                        }}
                        src={"/assets/images/streak-full.png"}
                        alt="fire"
                        className="absolute top-0 left-0 object-contain w-full h-full"
                    />
                )}
                <img
                    src={"/assets/images/streak-empty.png"}
                    alt="fire"
                    className="object-contain w-full h-full"
                />
            </div>
        );
    } else {
        return (
            <div className="w-10 h-10 rounded-full">
                <img
                    src={"/assets/images/streak-full.png"}
                    alt="fire"
                    className="object-contain w-full h-full"
                />
            </div>
        );
    }
};

export const StreakEmpty = () => {
    return (
        <div className="w-10 h-10 rounded-full">
            <img
                src={"/assets/images/streak-empty.png"}
                alt="gift"
                className="object-contain w-full h-full"
            />
        </div>
    );
};

export const Message = ({ msg }) => (
    <div className="flex flex-row items-center justify-center mt-3">
        <span className="text-base font-normal text-center text-dark-purple">
            {msg}
        </span>
    </div>
);

const QuestionMessage = ({ isSpeedy, isCorrect, speedyTransition }) => {
    switch (isCorrect) {
        case 0:
            return <Message msg={`❌ Incorrect answer`} />;
        case 1:
            return (
                <Message
                    msg={
                        speedyTransition
                            ? `Speedy Answer ⚡️`
                            : `✅ Correct Answer`
                    }
                />
            );
        case -1:
            return <Message msg={`⁉️ No attempt`} />;
    }

    return null;
};

interface pointsCounterProps {
    base: number;
    time: number;
    target: number;
    normalPoints: number;
    speedyPoints: number;
    splitTransition: boolean;
    className: string;
}

const PointsCounter: React.FC<pointsCounterProps> = ({
    base,
    time,
    target,
    normalPoints,
    speedyPoints,
    splitTransition = false,
    className,
}) => {
    return (
        <CountUp
            className={className}
            start={0}
            end={normalPoints}
            duration={time}
            prefix="+"
            onEnd={({ update }) => {
                if (splitTransition) {
                    setTimeout(() => {
                        update(target);
                    }, 4000);
                }
            }}
        />
    );
};

export const QuestionStatus = (props) => {
    const {
        points,
        isSpeedy,
        doubleUp,
        streak,
        isCorrect,
        setTotalPoints,
        totalPoints,
        endPointsCounter,
        endPointsState,
        totalQuestions,
        correctCount,
        set,
        scoreOffset,
    } = props;

    // calculate points to be shown in steps
    const normalPoints = isSpeedy ? points / 2 : points;
    const speedyPoints = points - normalPoints;

    const [playStarSound] = useSound(
        "/assets/sounds/Star given_futuristic-cartoon-pop-3-SBA-300463184.mp3",
    );

    const [playFirstPointsCounterSound, { duration: counterDuration }] =
        useSound(
            set.type == "practise_learn_practise"
                ? "/assets/sounds/Star given_futuristic-cartoon-pop-3-SBA-300463184.mp3"
                : "/assets/sounds/points_counter.mp3",
            {
                onend: () => {
                    console.log("First sound ended");
                    if (!isSpeedy) {
                        setPlayed(true);
                        if (set.type !== "practise_learn_practise")
                            setTotalPoints(totalPoints + points);
                        else
                            setTotalPoints(
                                `${correctCount + scoreOffset} /  ${
                                    totalQuestions + scoreOffset
                                }`,
                            );

                        endPointsState();
                    } else {
                        toggleSpeedyTransition(true);
                    }
                },
                onload: () => {
                    setSoundLoaded(true);
                    // console.log("First sound loaded");
                },
            },
        );

    const [playSecondPointsCounterSound] = useSound(
        "/assets/sounds/points_counter.mp3",
        {
            onend: () => {
                // console.log("Second sound ended");
                setPlayed(true);
                if (set.type !== "practise_learn_practise")
                    setTotalPoints(totalPoints + points);
                else
                    setTotalPoints(
                        `${correctCount + scoreOffset} /  ${
                            totalQuestions + scoreOffset
                        }`,
                    );
                endPointsState();
            },
        },
    );

    const [playZeroPointsSound, { duration: zeroDuration }] = useSound(
        "/assets/sounds/No points_failure-lose-game-over-out-of-time-sound-5-SBA-300462099.mp3",
        {
            onend: () => {
                setPlayed(true);
                // setTotalPoints(totalPoints + points);
                // setTotalPoints(`${totalPoints} /  ${totalQuestions}`)

                // endPointsState();
            },
        },
    );

    const [playSpeedySound, { duration: speedyDuration }] = useSound(
        "/assets/sounds/Speedy Answer_zap-sound-5-SBA-300286311.mp3",
        {
            onend: () => {
                setCounter(2);
                // console.log("Speedy sound ended");
            },
        },
    );

    const [soundLoaded, setSoundLoaded] = useState(false);
    const [played, setPlayed] = useState(false);
    const [counter, setCounter] = useState(0);
    const [speedyTransition, toggleSpeedyTransition] = useState(false);

    useEffect(() => {
        if (speedyTransition) {
            playSpeedySound();
            // console.log("Speedy sound initiated");
        }
    }, [speedyTransition]);

    useEffect(() => {
        console.log("Counter changed " + counter);
        switch (counter) {
            case -1:
                playZeroPointsSound();
                console.log("Zero points sound initiated");
                setTimeout(() => {
                    console.log(`overriding endPointsState`);
                    endPointsState();
                }, zeroDuration);

                break;
            case 1:
                if (set.type == "practise_learn_practise") {
                    playStarSound();

                    setTimeout(() => {
                        playFirstPointsCounterSound();
                    }, 800);
                } else playFirstPointsCounterSound();

                console.log("First sound initiated");
                break;
            case 2:
                playSecondPointsCounterSound();
                console.log("Second sound initiated");
                break;
        }
    }, [counter]);

    useEffect(() => {
        console.log("Mounted");
        if (!played && soundLoaded) {
            if (isCorrect === 1) {
                // hack to get the sound started
                setTimeout(() => {
                    setCounter(1);
                }, 1);
            } else {
                // hack to get the sound started
                setTimeout(() => {
                    setCounter(-1);
                }, 1);
            }
        }
    }, [soundLoaded]);
    return (
        <div className="flex flex-col items-center justify-center w-full h-full pt-0">
            <div className="flex flex-row items-center justify-center">
                {speedyTransition && isSpeedy ? (
                    <>
                        <GiftItem
                            img={
                                set.type != "practise_learn_practise"
                                    ? "/assets/images/points-coin.png"
                                    : isCorrect
                                    ? "/assets/images/star-full.png"
                                    : "/assets/images/star-empty.png"
                            }
                            fx="slide"
                        />
                        <GiftItem
                            img="/assets/images/gift-flash.png"
                            fx="stomp"
                        />
                    </>
                ) : (
                    <GiftItem
                        img={
                            set.type != "practise_learn_practise"
                                ? "/assets/images/points-coin.png"
                                : isCorrect
                                ? "/assets/images/star-full.png"
                                : "/assets/images/star-empty.png"
                        }
                        fx="pop"
                    />
                )}
            </div>
            <div className="flex flex-row items-center justify-center my-4">
                <PointsCounter
                    base={0}
                    time={1.6}
                    target={points}
                    normalPoints={normalPoints}
                    speedyPoints={speedyPoints}
                    splitTransition={isSpeedy}
                    className="font-extrabold text-center text-dark-purple text-7xl"
                />
            </div>

            <QuestionMessage {...props} speedyTransition={speedyTransition} />

            <div className="flex flex-row items-center mt-5">
                {[...Array(streak)].map((s, i) => (
                    <StreakFull fx={true} timeout={(i + 1) * 300} />
                ))}
            </div>
        </div>
    );
};
